import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import OTPInput, { ResendOTP } from 'otp-input-react'
import {
  Grid,
  Card,
  Divider,
  TextField,
  MenuItem,
  Container,
  Dialog,
  DialogTitle,
  CardContent,
  IconButton,
  FormControlLabel,
  Switch,
  Checkbox,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Typography,
  CircularProgress
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { ActionButton } from '../../../utils/ui_utils'
import { getUserType } from '../../../components/AuthGuard'
import { useSendOtp, useVerifyOtp, useFetchUserById } from '../../../modules/user/userHooks'

const userType = getUserType()

function OutletDetails({
  values,
  outletData,
  action,
  touched,
  onBlur,
  onChange,
  onErrors
}) {
  return (
    <>
      <TextField
        size="small"
        id="outlet_select"
        name="outlet_id"
        label="Outlet"
        value={values?.outlet_id}
        error={Boolean(touched.outlet_id && onErrors.outlet_id)}
        fullWidth
        onBlur={onBlur}
        margin="dense"
        onChange={onChange}
        variant="outlined"
        select
        disabled={(action == 'edit' && values?.active) || userType !== 'Owner'}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {outletData?.map((outlet) => (
          <MenuItem key={outlet?._id} value={outlet?._id}>
            {outlet?.name}
          </MenuItem>
        ))}
      </TextField>
    </>
  )
}

function SelectApp({
  values,
  apps,
  action,
  touched,
  onBlur,
  onChange,
  onErrors
}) {
  return (
    <>
      {action == 'new' ? (
        <TextField
          size="small"
          id="app_select"
          name="app_flavor_id"
          label="Application"
          value={values?.app_flavor_id}
          error={Boolean(touched.app_flavor_id && onErrors.app_flavor_id)}
          fullWidth
          onBlur={onBlur}
          margin="dense"
          onChange={onChange}
          variant="outlined"
          select
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {apps &&
            apps?.map((app) => (
              <MenuItem key={app?._id} value={app?._id}>
                {app?.flavor_name}
              </MenuItem>
            ))}
        </TextField>
      ) : values?.app_flavor?.flavor_name ? (
        <TextField
          size="small"
          id="app_name"
          name="app_name"
          label="Application"
          value={values?.app_flavor?.flavor_name}
          fullWidth
          onBlur={onBlur}
          margin="dense"
          variant="outlined"
          disabled
        >
          {values?.app_flavor?._id}
        </TextField>
      ) : (
        <></>
      )}
    </>
  )
}

function TerminalDialog({
  visible,
  title,
  apps,
  initialValues,
  onSubmit,
  onCancel,
  okText = 'Create',
  outletData,
  action,
  isOnlineTerminal = true
}) {
  if (initialValues == undefined) {
    if (isOnlineTerminal)
      initialValues = {
        name: '',
        outlet_id: '',
        app_flavor_id: '',
        is_hybrid: false
      }
    else
      initialValues = {
        name: ''
      }
  } else {
    if (isOnlineTerminal)
      initialValues = {
        name: initialValues?.name,
        outlet_id: initialValues?.outlet?._id,
        app_flavor_id: initialValues?.app_flavor?._id,
        active: initialValues?.active,
        is_hybrid: initialValues?.is_hybrid || false
      }
    else
      initialValues = {
        name: initialValues?.name
      }
  }

  if (outletData == undefined) outletData = []

  const validationSchema =
    action == 'new' && isOnlineTerminal
      ? Yup.object().shape({
          name: Yup.string()
            .required('Required')
            .min(2, 'Min length is 2 character')
            .max(30, 'Max length is 30 character'),
          outlet_id: Yup.string().required('Required'),
          app_flavor_id: Yup.string().required('Required')
        })
      : action == 'edit' && isOnlineTerminal && !initialValues?.outlet
      ? Yup.object().shape({
          name: Yup.string()
            .required('Required')
            .min(2, 'Min length is 2 character')
            .max(30, 'Max length is 30 character'),
          outlet_id: Yup.string().required('Required')
        })
      : Yup.object().shape({
          name: Yup.string()
            .required('Required')
            .min(2, 'Min length is 2 character')
            .max(30, 'Max length is 30 character')
        })

  const handleSubmit = (
    values,
    { resetForm, setErrors, setStatus, setSubmitting }
  ) => {
    onSubmit(values)
  }

  const [hybridConfirmOpen, setHybridConfirmOpen] = useState(false)
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState(null)
  const [OTP, setOTP] = useState('')
  const [showOtpUI, setShowOtpUI] = useState(false)
  const [otpError, setOtpError] = useState('')
  const [otpSending, setOtpSending] = useState(false)
  const [otpVerifying, setOtpVerifying] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const { userSendOtp, userSendOtploading } = useSendOtp()
  const { userVerifyOtp, userVerifyOtploading } = useVerifyOtp()
  const { userFetchById, userFetchByIdData } = useFetchUserById()

  // Fetch user data when dialog opens to get mobile number
  useEffect(() => {
    if (visible) {
      userFetchById()
    }
  }, [visible])

  const handleHybridChange = (event, setFieldValue) => {
    const newValue = event.target.checked
    if (newValue) {
      setPendingSetFieldValue(() => setFieldValue)
      setHybridConfirmOpen(true)
      setShowOtpUI(false)
      setOTP('')
      setOtpError('')
    } else {
      setFieldValue('is_hybrid', false)
    }
  }

  const handleSendOtp = () => {
    if (!userFetchByIdData?.user_name && !userFetchByIdData?.email) {
      setOtpError('Unable to get user details. Please try again.')
      return
    }
    setOtpSending(true)
    setOtpError('')
    const payload = {
      user: userFetchByIdData?.user_name || userFetchByIdData?.email || ''
    }
    userSendOtp(payload, (success, error, message) => {
      setOtpSending(false)
      if (error) {
        setOtpError(message || 'Failed to send OTP')
      } else {
        setShowOtpUI(true)
        setOtpError('')
      }
    })
  }

  const handleResendOtp = () => {
    setOTP('')
    handleSendOtp()
  }

  const handleVerifyAndConfirmHybrid = () => {
    if (OTP.length < 4) {
      setOtpError('Please enter valid 4-digit OTP')
      return
    }
    setOtpVerifying(true)
    setOtpError('')
    const payload = {
      user: userFetchByIdData?.user_name || userFetchByIdData?.email || '',
      otp: OTP
    }
    userVerifyOtp(payload, (success, error, message) => {
      setOtpVerifying(false)
      if (error) {
        setOtpError(message || 'Invalid OTP or OTP expired')
      } else {
        // OTP verified successfully, enable hybrid billing
        if (pendingSetFieldValue) {
          pendingSetFieldValue('is_hybrid', true)
        }
        setHybridConfirmOpen(false)
        setPendingSetFieldValue(null)
        setShowOtpUI(false)
        setOTP('')
        setOtpError('')
      }
    })
  }

  const handleCancelHybrid = () => {
    setHybridConfirmOpen(false)
    setPendingSetFieldValue(null)
    setShowOtpUI(false)
    setOTP('')
    setOtpError('')
    setAgreedToTerms(false)
  }

  return (
    <>
      <Dialog
        maxWidth="xs"
        fullWidth
        open={visible}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {title}
          <IconButton
            component="span"
            size="small"
            sx={{ ':hover': { backgroundColor: 'red', color: 'white' } }}
            onClick={onCancel}
            variant="contained"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Container sx={{ marginBottom: 5 }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              status,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit}>
                <Divider my={2} />
                <Card>
                  <CardContent>
                    <Grid container spacing={5} style={{ paddingTop: 10 }}>
                      <Grid item md>
                        <TextField
                          size="small"
                          name="name"
                          label="Name"
                          value={values?.name}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                          fullWidth
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>
                      {isOnlineTerminal == true && (
                        <Grid item md>
                          <OutletDetails
                            values={values}
                            outletData={outletData}
                            action={action}
                            touched={touched}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onErrors={errors}
                          />
                        </Grid>
                      )}
                    </Grid>
                    {/* {isOnlineTerminal == true && (<Grid item md> */}

                    <SelectApp
                      values={values}
                      apps={apps}
                      action={action}
                      touched={touched}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onErrors={errors}
                    />
                    {isOnlineTerminal && (
                      <Grid item md={12} sx={{ mt: 2 }}>
                        {/* State 1: Hybrid is enabled and saved in DB */}
                        {initialValues.is_hybrid ? (
                          <>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item>
                                <Switch
                                  checked={true}
                                  color="success"
                                  disabled
                                />
                              </Grid>
                              <Grid item>
                                <Typography
                                  variant="body1"
                                  sx={{ color: 'success.main', fontWeight: 500 }}
                                >
                                  Hybrid mode enabled
                                </Typography>
                              </Grid>
                            </Grid>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
                            >
                              This terminal is configured for hybrid billing. Inventory and customer features are disabled.
                            </Typography>
                          </>
                        ) : values.is_hybrid ? (
                          /* State 2: Pending - OTP verified but not saved yet */
                          <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                              Hybrid Billing will be enabled after you click {okText}.
                            </Alert>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item>
                                <Switch
                                  checked={values.is_hybrid}
                                  onChange={(e) => setFieldValue('is_hybrid', e.target.checked)}
                                  name="is_hybrid"
                                  color="info"
                                />
                              </Grid>
                              <Grid item>
                                <Typography
                                  variant="body1"
                                  sx={{ color: 'info.main', fontWeight: 500 }}
                                >
                                  Hybrid mode enabled
                                </Typography>
                              </Grid>
                            </Grid>
                          </>
                        ) : userType === 'Owner' ? (
                          /* State 3: Not enabled - show activation prompt (only for Owner) */
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                Need to activate hybrid mode?
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Button
                                variant="text"
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setPendingSetFieldValue(() => setFieldValue)
                                  setHybridConfirmOpen(true)
                                  setShowOtpUI(false)
                                  setOTP('')
                                  setOtpError('')
                                  setAgreedToTerms(false)
                                }}
                              >
                                Click here
                              </Button>
                            </Grid>
                          </Grid>
                        ) : null}
                      </Grid>
                    )}
                    {/* </Grid>) */}
                    {/* } */}
                  </CardContent>
                </Card>
                <Grid container justifyContent="flex-end">
                  <Grid item>
                    <ActionButton
                      variant="outlined"
                      size="medium"
                      type="submit"
                      mt={8}
                    >
                      {okText}
                    </ActionButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      </Dialog>
      <Dialog
        open={hybridConfirmOpen}
        onClose={handleCancelHybrid}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {'Enable Hybrid Billing?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" component="div">
            Enabling Hybrid Billing will have the following effects:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Inventory status will be automatically disabled in terminal settings.</li>
              <li>Customer features will not be available for this terminal.</li>
            </ul>
            This action can only be done once and is not reversible. Are you sure you want to proceed?
          </DialogContentText>

          {otpError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {otpError}
            </Alert>
          )}

          {!showOtpUI ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to enable Hybrid Billing and understand this action is irreversible.
                  </Typography>
                }
                sx={{ mt: 2, alignItems: 'flex-start' }}
              />
              <Typography
                variant="body2"
                sx={{ mt: 1, color: 'text.secondary' }}
              >
                Click "Send OTP" to receive a verification code on your registered mobile number
                {userFetchByIdData?.mobile && (
                  <strong>
                    {' '}(******{userFetchByIdData.mobile.slice(-4)})
                  </strong>
                )}.
              </Typography>
            </>
          ) : (
            <>
              <Divider sx={{ mt: 2, mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                OTP Verification
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Enter the 4-digit OTP sent to your registered mobile number.
              </Typography>
              <OTPInput
                value={OTP}
                onChange={setOTP}
                autoFocus
                OTPLength={4}
                otpType="number"
                disabled={otpVerifying}
                inputStyles={{
                  width: '40px',
                  height: '40px',
                  margin: '0 5px',
                  fontSize: '18px',
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
              />
              <ResendOTP
                onResendClick={handleResendOtp}
                disabled={otpSending}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelHybrid} color="primary" disabled={otpSending || otpVerifying}>
            Cancel
          </Button>
          {!showOtpUI ? (
            <Button
              onClick={handleSendOtp}
              color="primary"
              disabled={otpSending || !agreedToTerms}
              startIcon={otpSending && <CircularProgress size={16} />}
            >
              {otpSending ? 'Sending...' : 'Send OTP'}
            </Button>
          ) : (
            <Button
              onClick={handleVerifyAndConfirmHybrid}
              color="primary"
              disabled={otpVerifying || OTP.length < 4}
              startIcon={otpVerifying && <CircularProgress size={16} />}
            >
              {otpVerifying ? 'Verifying...' : 'Verify & Enable'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TerminalDialog
